import type { TooltipContentProps } from 'recharts'

import { Text } from '@buffer-mono/popcorn'

import { ChartColorDot } from '../ChartColorDot'
import { useChartSeriesById } from '../ChartSeriesContext'
import { formatChartValue } from '../chartFormatting'

import { isBreakdownRow } from './buildBreakdownRows'
import { formatSignedPercent } from './formatBreakdown'

import styles from './BreakdownBarChart.module.css'

import type { JSX } from 'react'

/** The trailing "(±N.N%)" — signed and coloured by direction (green up, red
 *  down, neutral flat). Renders nothing when there's no baseline (start of 0). */
function BreakdownPercent({ pct }: { pct: number | null }): JSX.Element | null {
  if (pct == null) return null
  const colorClass =
    pct > 0 ? styles.pctPositive : pct < 0 ? styles.pctNegative : undefined
  return <span className={colorClass}>{` (${formatSignedPercent(pct)})`}</span>
}

type BreakdownTooltipViewProps = Pick<
  Partial<TooltipContentProps<number, string>>,
  'active' | 'payload'
>

/**
 * Context-driven tooltip for the breakdown bar chart. Reads the hovered row from
 * Recharts and looks its series up in {@link ChartSeriesContext} for the colour,
 * icon, and label — so the tooltip can't drift from the bar it describes. Shows
 * the current value, the channel, and a `Started at X · gained/lost Y (±%)` line.
 */
export function BreakdownTooltipView({
  active,
  payload,
}: BreakdownTooltipViewProps): JSX.Element | null {
  const raw: unknown = payload?.[0]?.payload
  const row = isBreakdownRow(raw) ? raw : undefined
  const series = useChartSeriesById(row?.id ?? '')

  if (!active || row == null || series == null) return null

  const changeVerb = row.delta >= 0 ? 'gained' : 'lost'

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipRow}>
        <ChartColorDot color={series.color} />
        <Text size="sm" weight="bold">
          {formatChartValue(row.end, series.unit)}
        </Text>
        {series.icon}
        <Text size="sm">{series.label}</Text>
      </div>
      <Text size="sm" className={styles.tooltipSub}>
        {`Started at ${formatChartValue(row.start, series.unit)} · ${changeVerb} ${formatChartValue(Math.abs(row.delta), series.unit)}`}
        <BreakdownPercent pct={row.pct} />
      </Text>
    </div>
  )
}
